import { Controller } from "@hotwired/stimulus"
// Side-effect import: installs `window.HotwireNative.web`, the low-level bridge
// singleton used below. We deliberately do NOT extend BridgeComponent — its
// `static shouldLoad` returns false whenever the native shell doesn't advertise
// the component, which would stop this controller loading (and so stop photos
// auto-submitting) in every plain browser.
import "@hotwired/hotwire-native-bridge"
import { post } from "@rails/request.js"

/**
 * Inspections photo grid (CONEK-4653, spec §6.4; offline capture CONEK-7256).
 *
 * Auto-submits the photo upload form as soon as the inspector picks a file. On
 * the Turbo Native Android shell the file input opens the system camera/gallery
 * chooser via the standard web file-input bridge (no dedicated camera bridge in
 * wave 1); on submit the multipart form posts to the page's photo endpoint and
 * Turbo replaces the grid with the new thumbnail.
 *
 * CONEK-8801 — the grid sits beside the pager's answers form, not inside it.
 * The endpoint used to redirect back to the page, which re-rendered the
 * answers form from the server and reset every answer not saved yet. It now
 * answers a Turbo submit (add, or a thumbnail's remove) with a Turbo Stream
 * that replaces only the photo block, so a success needs nothing from here:
 * the new grid connects idle. This controller only marks the grid busy for the
 * wait, and recovers when the request never completed and this grid is still
 * the one on screen.
 *
 * CONEK-7256 — offline capture. An inspector photographing damage deep inside a
 * warehouse has no signal, and the multipart submit simply failed at the network
 * layer: the photo was lost. `bridge--offline-queue` could not help either,
 * because it serialises a form with `new URLSearchParams(formData)`, which
 * stringifies a File to the literal "[object File]".
 *
 * So when ALL of these hold — the tenant has `mobile.rep.offline` (meta tag),
 * the device is offline right now, and the native shell actually advertises the
 * `offline-queue` component — we read the image into a base64 data URL and hand
 * it to that SAME native component as a form-urlencoded body
 * (`file_data_url=…`). It rides the existing Room-backed queue and
 * `OfflineFlushWorker` untouched (no Android release needed), and
 * `Mobile::Inspections::PhotosController` decodes it back into an attachment on
 * replay. The queue's stable `Idempotency-Key` means a crash-window resend does
 * not create a duplicate photo.
 *
 * If ANY of the three is false — online, flag off, plain browser with no native
 * queue to hand the image to — behaviour is exactly the pre-existing
 * `requestSubmit()`. Never swallow a submit we cannot actually queue: that would
 * lose the photo silently, which is the failure this ticket exists to fix.
 *
 * CONEK-7589 — desktop drag-and-drop onto the add tile. Dropped files upload
 * SEQUENTIALLY, one `post` per file, because the endpoint takes a single `file`
 * and redirects. Stuffing them into the input and calling `requestSubmit()`
 * would navigate away after the first and lose the rest silently. A drop never
 * touches the native bridge or `requestSubmit()`.
 *
 * A drop asks for HTML, and to HTML the endpoint still redirects on both
 * success and reject, so the status code cannot tell them apart. Instead we
 * count `[data-photo-id]` thumbnails in the returned page: a file succeeded only
 * if the count grew past what we expected. Once anything succeeded we swap this
 * grid for the one in the last successful response — never the whole page,
 * which would reset the unsaved answers (CONEK-8801) — and carry the error
 * summary (unsupported / too many / failed) to the new grid's controller in
 * sessionStorage, keyed by the grid's page; otherwise it is shown inline. If the
 * grid leaves the document (a Turbo navigation) before the uploads finish, we
 * do nothing more: the photos are already saved server-side, and neither a new
 * grid nor a summary belongs on the page the user moved to. While busy, the
 * input is disabled so the tile cannot open the file picker mid-upload.
 *
 * CONEK-8802 — the swipe pager holds every page of the inspection in one
 * document, one grid each, and changes the URL as the inspector swipes. So a
 * grid is identified by its page's photo block (the nearest ancestor with an
 * id), not by the URL: the summary is keyed by it, the returned page is
 * searched for the grid inside the same block, and a drop that finishes after
 * a swipe still swaps its own grid.
 *
 * CONEK-9165 — two ways to add a photo. Android Chrome opens ONLY the gallery
 * for a plain `<input type="file" accept="image/*">`, so the form now carries
 * two inputs under the same name: a camera input (`capture="environment"`,
 * "Take photo") and a gallery input ("Choose from gallery"). Both are `input`
 * targets with the same `change` action. `submit` reads the input that changed
 * and, right before `requestSubmit()`, disables every OTHER input: a disabled
 * control is left out of the form body, whereas an empty file input would post
 * a blank part under the same name next to the real photo. Busy state, the
 * failure reset and the offline capture cover every input; drop and its
 * `accept` check use the gallery input (the dropzone's control).
 *
 * Markup (rendered by Ui::PhotoGridComponent; the two-input form comes from
 * conek_ui 0.3.67, camera first): the gallery input is found through the
 * dropzone label (`control`), never by position, and each tile through its
 * input's `labels`.
 *   <div data-controller="inspections--photo-grid"
 *        data-inspections--photo-grid-free-slots-value="…" (+ *-message-value)>
 *     <form data-inspections--photo-grid-target="form" ...>
 *       <label for="camera-id">
 *       <input type="file" id="camera-id" capture="environment"
 *              data-inspections--photo-grid-target="input"
 *              data-action="change->inspections--photo-grid#submit" />
 *       <label for="gallery-id" data-inspections--photo-grid-target="dropzone"
 *              data-action="dragover->…#dragOver dragleave->…#dragLeave drop->…#drop">
 *       <input type="file" id="gallery-id" data-inspections--photo-grid-target="input"
 *              data-action="change->inspections--photo-grid#submit" />
 *     </form>
 *     <p data-inspections--photo-grid-target="error" role="alert"></p>
 *   </div>
 */

// The native bridge component that owns the Room-backed queue. Shared verbatim
// with `bridge--offline-queue`; the Android side persists whatever it is given,
// so reusing it needs no native change.
const OFFLINE_QUEUE_COMPONENT = "offline-queue"

// Mirrors Mobile::Inspections::PhotosController::MAX_PHOTO_BYTES. A write the
// server is guaranteed to reject would sit at the head of the FIFO queue burning
// retries until OfflineFlushWorker dead-letters it — losing the photo anyway,
// just later and silently. Don't capture it.
const MAX_PHOTO_BYTES = 25 * 1024 * 1024

const SUMMARY_KEY_PREFIX = "inspections--photo-grid:summary:"

export default class extends Controller {
  static targets = ["form", "input", "dropzone", "error"]

  static values = {
    freeSlots: Number,
    tooManyMessage: String,
    unsupportedMessage: String,
    failedMessage: String,
  }

  connect() {
    // A Turbo cache snapshot taken mid-upload keeps the busy markers (and the
    // disabled input) in the DOM. This instance has no upload in flight, so
    // start idle.
    this.setBusy(false)

    // The forms are rendered by Ui::PhotoGridComponent in the conek-ui gem, so
    // listen here rather than adding data-action to its markup.
    this.element.addEventListener("turbo:submit-start", this.submitStarted)
    this.element.addEventListener("turbo:submit-end", this.submitEnded)

    this.showStoredSummary()
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.submitStarted)
    this.element.removeEventListener("turbo:submit-end", this.submitEnded)
  }

  // Fired once Turbo has built the request body, so disabling the input here
  // cannot drop the picked file from it.
  submitStarted = () => {
    this.clearError()
    this.setBusy(true)
  }

  submitEnded = (event) => {
    // The stream already replaced this grid: the new one is idle.
    if (!this.element.isConnected) return

    this.setBusy(false)

    // Any response speaks for itself: a stored photo replaces the grid, a
    // rejected one also brings a toast saying why. No response at all means
    // the request never completed, so nothing on screen says it failed.
    if (event.detail?.success || event.detail?.fetchResponse) return

    // Cleared so that picking the same photo again fires `change`.
    this.inputTargets.forEach((input) => {
      input.value = ""
    })
    this.nextFrame(() => this.showError(this.failedMessageValue))
  }

  showStoredSummary() {
    const key = this.summaryKey()

    let summary = null
    try {
      summary = window.sessionStorage.getItem(key)
    } catch {
      return
    }
    if (!summary) return

    try {
      window.sessionStorage.removeItem(key)
    } catch {
      // Storage unavailable: the summary may show again on the next load,
      // which is harmless.
    }

    // The error element is a `role="alert"` live region. Changing its content
    // in the same task that rendered it may not be announced, so wait a frame.
    this.nextFrame(() => this.showError(summary))
  }

  nextFrame(callback) {
    if (typeof window.requestAnimationFrame === "function")
      window.requestAnimationFrame(callback)
    else setTimeout(callback, 0)
  }

  dragOver(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget) this.dropzoneTarget.dataset.dragging = ""
  }

  dragLeave(event) {
    if (!this.hasDropzoneTarget) return
    // Moving between the tile's own children fires dragleave on the tile too.
    const related = event.relatedTarget
    if (related instanceof Node && this.dropzoneTarget.contains(related)) return
    delete this.dropzoneTarget.dataset.dragging
  }

  async drop(event) {
    // Captured before anything is awaited: the grid is swapped at the end.
    const originKey = this.summaryKey()

    event.preventDefault()
    if (this.hasDropzoneTarget) delete this.dropzoneTarget.dataset.dragging
    if (this.busy) return

    const files = Array.from(event.dataTransfer?.files ?? [])
    if (files.length === 0) return
    if (!this.hasFormTarget || !this.hasInputTarget) return

    this.clearError()

    const supported = files.filter((file) => this.matchesAccept(file))
    const toUpload = supported.slice(0, Math.max(this.freeSlotsValue, 0))

    const messages = []
    if (supported.length < files.length)
      messages.push(this.unsupportedMessageValue)
    if (toUpload.length < supported.length)
      messages.push(this.tooManyMessageValue)

    let result = { succeeded: 0, failed: 0, grid: null }
    if (toUpload.length > 0) {
      this.setBusy(true)
      try {
        result = await this.uploadSequentially(toUpload)
      } finally {
        this.setBusy(false)
      }
    }
    const { succeeded, failed, grid } = result

    // The user moved on mid-upload (Turbo navigation, or the grid was
    // replaced). The uploaded photos are already saved; swapping the grid or
    // storing a summary now would act on a page that is no longer this one.
    if (!this.element.isConnected) return

    if (failed > 0) messages.push(this.failedMessageValue)

    const summary = messages.filter(Boolean).join(" ")

    if (succeeded > 0 && grid) {
      if (summary) this.storeSummary(originKey, summary)
      // The last successful response already holds every photo the drop
      // stored. Only this grid is swapped; the new one's controller connects
      // and shows the stored summary.
      this.element.replaceWith(grid)
      return
    }

    if (!summary) return
    // Cleared first and set a frame later, so the alert region announces a
    // repeat of the very same message too.
    this.clearError()
    this.nextFrame(() => this.showError(summary))
  }

  async uploadSequentially(files) {
    const url =
      this.formTarget.getAttribute("action") || window.location.pathname
    const fieldName = this.referenceInput.name || "file"
    let expected = this.element.querySelectorAll("[data-photo-id]").length
    let succeeded = 0
    let failed = 0
    let grid = null

    // Awaited one at a time on purpose: see the CONEK-7589 note above.
    for (const file of files) {
      const body = new FormData()
      body.append(fieldName, file)

      let ok = false
      try {
        const response = await post(url, { body, responseKind: "html" })
        if (response.ok) {
          const returned = this.gridIn(await response.text)
          const count = returned
            ? returned.querySelectorAll("[data-photo-id]").length
            : 0
          if (count > expected) {
            expected = count
            grid = returned
            ok = true
          }
        }
      } catch {
        ok = false
      }

      if (ok) succeeded += 1
      else failed += 1
    }

    return { succeeded, failed, grid }
  }

  // The photo grid in a returned page. Counted in the same scope as the
  // starting count taken from `this.element`: a `[data-photo-id]` elsewhere on
  // the page must not read as a success.
  gridIn(html) {
    if (typeof html !== "string") return null
    const doc = new DOMParser().parseFromString(html, "text/html")
    const scope = this.scopeId()
    const holder = (scope && doc.getElementById(scope)) || doc
    return holder.querySelector('[data-controller~="inspections--photo-grid"]')
  }

  // Same semantics as conek-ui's `file_input_controller.js#_matchesAccept`,
  // read at drop time so the input's current `accept` always wins.
  matchesAccept(file) {
    const tokens = (this.referenceInput.accept || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
    if (tokens.length === 0) return true

    const name = (file.name || "").toLowerCase()
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : ""
    const type = (file.type || "").toLowerCase()

    return tokens.some((token) => {
      if (token.startsWith(".")) return ext === token
      if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1))
      return type === token
    })
  }

  // The input drop and the `accept` check read: the dropzone's own control (the
  // gallery input) when it has one, else the first input.
  get referenceInput() {
    if (this.hasDropzoneTarget && this.dropzoneTarget.control)
      return this.dropzoneTarget.control
    return this.inputTargets[0]
  }

  // Disabling an input also stops its label (the tile) from opening the file
  // picker, by click or keyboard, while an upload is running. Every input and
  // every label pointing at one is marked, so the camera tile looks busy too.
  setBusy(busy) {
    this.busy = busy
    const tiles = new Set()
    if (this.hasDropzoneTarget) tiles.add(this.dropzoneTarget)
    this.inputTargets.forEach((input) => {
      input.disabled = busy
      Array.from(input.labels ?? []).forEach((label) => tiles.add(label))
    })
    tiles.forEach((tile) => {
      if (busy) tile.dataset.busy = ""
      else delete tile.dataset.busy
    })
    if (busy) this.element.setAttribute("aria-busy", "true")
    else this.element.removeAttribute("aria-busy")
  }

  // Keyed by the PAGE, not the form: a drop that fills the grid swaps in one at
  // the limit, where the component renders no form at all, and the summary
  // (e.g. "too many") must still show there.
  summaryKey(scope = this.scopeId()) {
    return `${SUMMARY_KEY_PREFIX}${scope}`
  }

  // The grid's page: the id of the block it sits in (the pager's per-page photo
  // block), or the URL path when it sits in none.
  scopeId() {
    return (
      this.element.parentElement?.closest("[id]")?.id ||
      window.location.pathname
    )
  }

  storeSummary(key, summary) {
    try {
      window.sessionStorage.setItem(key, summary)
    } catch {
      // Storage unavailable: the reload still shows the new thumbnails; only
      // the summary is lost.
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
  }

  async submit(event) {
    if (!this.hasInputTarget || !this.hasFormTarget) return

    const input = this.pickedInput(event)
    const file = input?.files?.[0]
    if (!file) return

    if (this.shouldCaptureOffline(file)) {
      await this.captureOffline(input, file)
      return
    }

    this.submitFrom(input)
  }

  // The input that fired `change`; without an event, the first one holding a file.
  pickedInput(event) {
    const source = event?.currentTarget ?? event?.target
    if (this.inputTargets.includes(source)) return source
    return this.inputTargets.find((input) => input.files?.[0])
  }

  // The one place the form is submitted. Every other input is disabled first so
  // only `input`'s file is in the body (a disabled control is not submitted);
  // `setBusy(false)` re-enables them when the request ends. Callers that were
  // busy (offline capture) call `setBusy(false)` BEFORE this, so the picked
  // input is enabled when the form body is built.
  //
  // Turbo may never start the submission (`data-turbo="false"`, a cancelled
  // `turbo-confirm`, a prevented submit): then no `turbo:submit-end` would ever
  // re-enable the others. A frame later, if the grid is still not busy, no
  // submission started, so give the inputs back.
  submitFrom(input) {
    this.inputTargets.forEach((other) => {
      if (other !== input) other.disabled = true
    })
    this.formTarget.requestSubmit()
    this.nextFrame(() => {
      if (!this.element.isConnected || this.busy) return
      this.inputTargets.forEach((other) => {
        other.disabled = false
      })
    })
  }

  // Server-side `mobile.rep.offline` for this tenant, surfaced by the mobile
  // layout as a meta tag — the same gate bridge--offline-queue uses.
  offlineCaptureEnabled() {
    return (
      document.querySelector('meta[name="mobile-rep-offline"]')?.content ===
      "true"
    )
  }

  get bridge() {
    return window.HotwireNative?.web
  }

  nativeQueueAvailable() {
    return Boolean(this.bridge?.supportsComponent?.(OFFLINE_QUEUE_COMPONENT))
  }

  shouldCaptureOffline(file) {
    if (!this.offlineCaptureEnabled()) return false
    if (navigator.onLine !== false) return false
    if (!this.nativeQueueAvailable()) return false

    return file.size > 0 && file.size <= MAX_PHOTO_BYTES
  }

  async captureOffline(input, file) {
    // Busy from the pick until native acks: a second capture while the first is
    // still being read or queued would make the Android app prune the first
    // capture file under us. Every early-out below clears it BEFORE any
    // `submitFrom`, because a disabled input is left out of the form body.
    this.setBusy(true)

    const dataUrl = await this.readAsDataUrl(file)
    if (!this.element.isConnected) return
    if (!dataUrl) {
      // Unreadable file: fall through to the normal submit rather than queueing
      // something empty that would only ever replay as a rejected write.
      this.setBusy(false)
      this.submitFrom(input)
      return
    }

    const body = new URLSearchParams({ file_data_url: dataUrl }).toString()

    // Wait for the native ack before believing the photo is safe. Handing the
    // message to the bridge is NOT the same as the row reaching Room: the
    // insert happens in a coroutine on the other side and can fail. Clearing
    // the picker on the optimistic assumption would lose the photo silently —
    // one layer down, the exact bug this controller exists to fix.
    //
    // The inline `callback` is a first-class part of the low-level API, not a
    // BridgeComponent-only affordance: `Bridge#send` destructures
    // `{component, event, data, callback}`, stores it in its own
    // `#pendingCallbacks` map keyed by the generated message id, and
    // `Bridge#receive` → `executeCallbackFor` routes the reply back by that id
    // with no component registration involved. `BridgeComponent#send` is a thin
    // wrapper that builds the very same object and calls `this.bridge.send`.
    // Pinned against the real library (not a mock) by the contract block in
    // `__tests__/photo_grid_controller.test.ts`, so an upgrade that changes it
    // fails CI instead of silently turning this path into a no-op.
    const messageId = this.bridge.send({
      component: OFFLINE_QUEUE_COMPONENT,
      event: "enqueue",
      data: {
        method: "POST",
        url: this.formTarget.getAttribute("action") || window.location.pathname,
        body,
        content_type: "application/x-www-form-urlencoded",
        csrf_token:
          document.querySelector('meta[name="csrf-token"]')?.content ?? "",
        // Same envelope BridgeComponent#send adds, so the native side sees an
        // identical message shape whichever controller sent it.
        metadata: { url: window.location.href },
      },
      callback: (message) => this.handleEnqueueReply(message, input, file),
    })

    // `Bridge#send` returns the message id, or null when it could not deliver —
    // the component stopped being supported between our check and this call.
    // Nothing is queued and no callback will ever fire, so hand the submit back.
    if (messageId == null) {
      this.setBusy(false)
      this.submitFrom(input)
    }
  }

  handleEnqueueReply(message, input, file) {
    const data =
      message?.data !== null &&
      message?.data !== undefined &&
      typeof message.data === "object"
        ? message.data
        : message

    // The element can be gone by the time native answers (Turbo navigation).
    if (!this.element.isConnected) return
    if (!this.hasFormTarget || !this.hasInputTarget) return

    // The ack ends the busy window, queued or not; before `submitFrom` so the
    // picked input is enabled when the body is built.
    this.setBusy(false)

    if (data?.queued) {
      // Now the image really is the queue's responsibility. Clear the picker so
      // the next pick does not re-send this same photo.
      input.value = ""
      this.dispatch("queued", { detail: { filename: file.name } })
      return
    }

    // Native could not persist it. Leave the file selected and put the submit
    // back in the user's hands rather than reporting a success that never was.
    this.dispatch("queue-failed", { detail: { filename: file.name } })
    this.submitFrom(input)
  }

  readAsDataUrl(file) {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () =>
        resolve(typeof reader.result === "string" ? reader.result : null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(file)
    })
  }
}
